import { useState, useEffect, useRef } from "react";
import { Mail, MapPin, Facebook, Heart } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useScrollReveal(ref) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible")); }),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

export default function ContactPage() {
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState("");
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) return;

    setContactStatus("");

    // Build URL-encoded payload for Netlify Forms (must include form-name)
    const formData = new URLSearchParams();
    formData.append("form-name", "contact");
    formData.append("name", contactForm.name);
    formData.append("email", contactForm.email);
    formData.append("message", contactForm.message);

    // Submit to Netlify Forms (PRIMARY) and backend API (best-effort) in parallel.
    // Netlify is the authoritative receiver because the user enabled Netlify Forms.
    const netlifyPromise = fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: formData.toString(),
    })
      .then((r) => r.ok)
      .catch(() => false);

    const apiPromise = axios
      .post(`${API}/contact`, contactForm)
      .then(() => true)
      .catch(() => false);

    const [netlifyOk, apiOk] = await Promise.all([netlifyPromise, apiPromise]);

    if (netlifyOk || apiOk) {
      setContactStatus("Thank you for reaching out. We'll get back to you soon.");
      setContactForm({ name: "", email: "", message: "" });
    } else {
      setContactStatus("Something went wrong. Please try again.");
    }
    setTimeout(() => setContactStatus(""), 5000);
  };

  return (
    <main ref={sectionRef} className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1A3A4A] to-[#1A3A4A]/90 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#0E8DB5]/15 rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="animate-on-scroll max-w-3xl">
            <p className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#7CC8E0] mb-4">
              <span className="inline-block w-10 h-[2px] bg-[#0E8DB5] mr-3 align-middle" />Reach Out
            </p>
            <h1 data-testid="contact-page-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-tight mb-6">
              Contact <span className="text-[#7CC8E0]">Us</span>
            </h1>
            <p className="text-base sm:text-lg font-sans font-medium text-white/80">
              Whether you need resources, want to partner with us, or simply want to talk — every message matters to us.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-20 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Left: Contact info */}
            <div className="animate-on-scroll">
              <h2 className="text-3xl font-serif font-bold text-earth mb-6">We're Here <span className="gradient-text">for You</span></h2>
              <p className="text-base font-sans font-medium text-earth-muted leading-relaxed mb-10">
                Whether you need resources, want to partner with us, or simply want to talk — reach out. We respond to every message with care and urgency.
              </p>

              <div className="flex flex-col gap-6">
                <a href="mailto:info@tysservices.org" data-testid="contact-email-link" className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#0E8DB5]/30 transition-all duration-300 no-underline group hover:shadow-md">
                  <div className="w-12 h-12 rounded-xl bg-[#0E8DB5]/10 flex items-center justify-center group-hover:bg-[#0E8DB5] transition-colors">
                    <Mail className="w-6 h-6 text-[#0E8DB5] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs font-sans tracking-wider uppercase text-earth-muted font-semibold">Email</p>
                    <p className="text-base font-sans font-bold text-earth">info@tysservices.org</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#C8DDD8]/50">
                  <div className="w-12 h-12 rounded-xl bg-[#3DAA35]/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#3DAA35]" />
                  </div>
                  <div>
                    <p className="text-xs font-sans tracking-wider uppercase text-earth-muted font-semibold">Location</p>
                    <p className="text-base font-sans font-bold text-earth">Massachusetts, United States</p>
                  </div>
                </div>

                <a href="https://www.facebook.com/profile.php?id=61579996282258" target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-link" className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#0E8DB5]/30 transition-all duration-300 no-underline group hover:shadow-md">
                  <div className="w-12 h-12 rounded-xl bg-[#0E8DB5]/10 flex items-center justify-center group-hover:bg-[#0E8DB5] transition-colors">
                    <Facebook className="w-6 h-6 text-[#0E8DB5] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs font-sans tracking-wider uppercase text-earth-muted font-semibold">Social</p>
                    <p className="text-base font-sans font-bold text-earth">Follow us on Facebook</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: Form - Netlify compatible */}
            <div className="animate-on-scroll delay-200">
              <div className="p-8 md:p-10 rounded-3xl bg-white border border-[#C8DDD8]/50 shadow-sm">
                <h3 className="text-2xl font-serif font-bold text-earth mb-2">Send a Message</h3>
                <p className="text-sm font-sans text-earth-muted mb-6">We'll get back to you as soon as possible.</p>
                <form
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  {/* Netlify hidden fields */}
                  <input type="hidden" name="form-name" value="contact" />
                  <p className="hidden"><label>Don't fill this out: <input name="bot-field" /></label></p>

                  <input
                    data-testid="contact-name-input"
                    type="text" name="name" placeholder="Your Name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-5 py-3.5 rounded-xl bg-[#F8FAFB] border border-[#C8DDD8] text-earth placeholder-earth-muted/50 font-sans text-sm focus:outline-none focus:border-[#0E8DB5] focus:ring-2 focus:ring-[#0E8DB5]/20 transition-all duration-300"
                    required
                  />
                  <input
                    data-testid="contact-email-input"
                    type="email" name="email" placeholder="Your Email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-5 py-3.5 rounded-xl bg-[#F8FAFB] border border-[#C8DDD8] text-earth placeholder-earth-muted/50 font-sans text-sm focus:outline-none focus:border-[#0E8DB5] focus:ring-2 focus:ring-[#0E8DB5]/20 transition-all duration-300"
                    required
                  />
                  <textarea
                    data-testid="contact-message-input"
                    name="message" placeholder="Your Message" rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full px-5 py-3.5 rounded-xl bg-[#F8FAFB] border border-[#C8DDD8] text-earth placeholder-earth-muted/50 font-sans text-sm focus:outline-none focus:border-[#0E8DB5] focus:ring-2 focus:ring-[#0E8DB5]/20 transition-all duration-300 resize-none"
                    required
                  />
                  <button data-testid="contact-submit-button" type="submit" className="btn-pill btn-primary w-full justify-center">
                    Send Message
                  </button>
                  {contactStatus && (
                    <p data-testid="contact-status" className="text-sm font-sans text-[#3DAA35] text-center font-semibold">{contactStatus}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation CTA */}
      <section className="py-16 bg-[#1A3A4A] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4">Support Our <span className="text-[#7CC8E0]">Mission</span></h2>
          <p className="text-base font-sans text-white/70 mb-8">Every donation provides caregivers with essential resources and compassionate support.</p>
          <a href="https://20d0a0a2-9bc3-4878-a451-dbaa4c9318b7.paylinks.godaddy.com/bfd32eeb-318a-425f-b9fb-26a" target="_blank" rel="noopener noreferrer" className="btn-pill btn-primary text-base px-10 py-4 no-underline inline-flex shadow-lg shadow-[#0E8DB5]/30">
            <Heart className="w-5 h-5" /> Donate Now
          </a>
        </div>
      </section>
    </main>
  );
}
